import { useEffect, useMemo } from "react";
import { useSelector } from "react-redux";
import { NavLink, useNavigate, useParams } from "react-router";

import * as Api from "@/api";
import { NEW_OUTBOUND } from "@/constants/New";
import Avatar from "@/design/components/Avatar";
import DeleteFlow from "@/design/components/DeleteFlow";
import HeaderCard from "@/design/components/HeaderCard";
import SaveIndicator from "@/design/components/SaveIndicator";
import FormGroup from "@/design/forms/FormGroup";
import InputAutocomplete from "@/design/forms/InputAutocomplete";
import InputNumber from "@/design/forms/InputNumber";
import InputText from "@/design/forms/InputText";
import { number, string } from "@/utils/hooks/endorse";
import useSave, { SaveState } from "@/utils/hooks/useSave";
import { toggleLoadingState } from "@/utils/utility";
import Valkyrie, { viArrowLeft, viArrowRightFromBracket } from "@sippy-platform/valkyrie";

import { Container, Grid, IconButton, Stack } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";

import outboundReducer from "./outboundReducer";
import PrefixEditor from "./prefixEditor/PrefixEditor";

function Outbound() {
  const { id } = useParams();
  const navigate = useNavigate();
  const theme = useTheme();
  const matches = useMediaQuery(theme.breakpoints.up("md"));

  const pbx = useSelector((state) => state.domain.pbx);
  const phoneNumberStore = useSelector((state) => state.domain.phoneNumbers);
  const inboundRouteStore = useSelector((state) => state.domain.inboundRoutes);

  const outgoingNumberList = useMemo(() => {
    return (
      phoneNumberStore.list?.map((phoneNumber) => ({
        did: phoneNumber.did,
        name: inboundRouteStore.list.find((inbound) => inbound.did === phoneNumber.did)?.name || "",
      })) || []
    );
  }, [inboundRouteStore.list, phoneNumberStore.list]);

  // Get general data
  useEffect(() => {
    Promise.all([Api.getPbx(), Api.getInbounds(), Api.getCurrentPhoneNumbers()]);
  }, []);

  const {
    value: outbound,
    dispatch,
    setSaveState,
    formState,
    errors,
    allErrors,
    save,
  } = useSave({
    initialValue: NEW_OUTBOUND,
    reducer: outboundReducer,
    validator: () => ({
      name: string()
        .required({ message: "Name is required." })
        .length([1, 60], { message: "Name must be at most 60 characters." }),
      prefix: number()
        .required({ message: "Prefix is required." })
        .min(1, { message: "Prefix must be between 1 and 99999." })
        .max(99999, { message: "Prefix must be between 1 and 99999." })
        .pattern("^[1-9]*$", { message: "Prefix cannot contain a 0." }),
      callerId: number().required({ message: "Number is required." }),
    }),
    onSubmit: () => Api.storeOutbound(outbound),
    onSuccess: (response) => {
      if (!outbound.id) {
        dispatch({ type: "id", value: response.data.outboundRule.id });
        navigate(`../${response.data.outboundRule.id}`);
      }

      Promise.resolve(Api.getOutbounds());
    },
  });

  useEffect(() => {
    let ignore = false;

    if (Number(id) === outbound.id || !id) {
      return;
    }

    if (id === "new") {
      Api.cancelActiveGetRequests();
      dispatch({ type: "set", value: NEW_OUTBOUND });

      return () => {
        ignore = true;
      };
    }

    toggleLoadingState(true);

    Promise.resolve(Api.getOutbound({ id: Number(id) }))
      .then((response) => !ignore && dispatch({ type: "set", value: response.data.outboundRule }))
      .finally(() => toggleLoadingState(false));

    return () => {
      ignore = true;
    };
  }, [dispatch, id, outbound.id]);

  // Handle changes
  const formHandler = (property, value) => {
    dispatch({ type: property, value });
    setSaveState(SaveState.UNSAVED);
  };

  // Delete
  function onDeleteFinish() {
    Promise.resolve(Api.getOutbounds()).then(() => navigate(".."));
  }

  return (
    <Container>
      <title>{`${outbound.name || "Outbound rule"} \u00b7 ${pbx.name} \u00b7 ${import.meta.env.SIPPY_APP_TITLE}`}</title>
      <Stack
        sx={{
          gap: 2,
        }}
      >
        <HeaderCard
          avatar={
            !matches ? (
              <IconButton variant="avatar" component={NavLink} to={".."}>
                <Valkyrie icon={viArrowLeft} />
                <Avatar text={outbound.name} icon={viArrowRightFromBracket} />
              </IconButton>
            ) : (
              <Avatar text={outbound.name} icon={viArrowRightFromBracket} />
            )
          }
          action={
            <>
              <SaveIndicator formState={formState} errors={allErrors} save={save} />
              <DeleteFlow
                key={outbound.id || "nk"}
                isActionable={!!outbound?.id}
                onDelete={() => Api.deleteOutbound(outbound)}
                name={`${outbound.name} (${outbound.prefix})`}
                deleteInfo="Are you sure you want to delete this outbound rule?"
                onDeleteFinish={onDeleteFinish}
              />
            </>
          }
          title={outbound.name || "Unnamed outbound rule"}
          subheader={outbound.prefix || "No prefix set"}
        />
        <FormGroup title="Identity">
          <Grid size={{ xs: 12, sm: 6, md: 12, lg: 6 }}>
            <InputText required id="name" label="Name" onChange={formHandler} value={outbound.name} error={errors} />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 12, lg: 6 }}>
            <InputNumber
              required
              id="prefix"
              label="Prefix"
              onChange={formHandler}
              value={outbound.prefix}
              error={errors}
              helperText="The prefix cannot contain a zero."
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 12, lg: 6 }}>
            <InputAutocomplete
              required
              id="callerId"
              label="Number"
              onChange={formHandler}
              value={outbound.callerId}
              selectValue="did"
              selects={outgoingNumberList}
              getOptionLabel={(option) => (option.name ? `${option.did} \u00b7 ${option.name}` : option.did)}
              renderOptionLabel={(option) => (
                <>
                  {option.did}
                  {option.name !== "" && <span style={{ opacity: 0.6 }}>&nbsp;&middot; {option.name}</span>}
                </>
              )}
              error={errors}
            />
          </Grid>
        </FormGroup>
        {outbound.id && <PrefixEditor key={outbound.id} outboundRule={outbound} outboundDispatch={dispatch} />}
      </Stack>
    </Container>
  );
}

export default Outbound;
